import React, { useEffect, useState } from 'react';
import { Button, Col, Row, Tag, Typography } from 'antd';
import { NotificationOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useLanguage } from '../store/LanguageContext';
import { usePageSeo } from '../utils/usePageSeo';

const { Title, Paragraph, Text } = Typography;

interface NewsArticle {
  title: string;
  summary: string;
  category: string;
  date: string;
  url?: string;
  slug?: string;
  cover?: string;
}

const NewsPage: React.FC = () => {
  const { language } = useLanguage();
  const zh = language === 'zh-CN';
  usePageSeo({
    title: zh ? 'AI 选型短文' : 'AI buying notes',
    description: zh
      ? '按任务阅读 AI 工具选型短文：写作、出图、视频、编程与国内可用性对照。'
      : 'Task-based AI tool buying notes — writing, images, video, coding, and China access.',
    path: '/news',
  });
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [updatedAt, setUpdatedAt] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/content/news.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setUpdatedAt(data.updatedAt || '');
        setArticles(Array.isArray(data.articles) ? data.articles : []);
      } catch {
        /* empty */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const articlePath = (article: NewsArticle) => {
    if (article.slug) return `/news/${article.slug}`;
    if (article.url) {
      try {
        const u = new URL(article.url, window.location.origin);
        if (u.pathname.startsWith('/news/')) return u.pathname;
        return u.pathname + u.search;
      } catch {
        return article.url.replace(/^https?:\/\/[^/]+/, '') || '/news';
      }
    }
    return '/news';
  };

  return (
    <div className="insights-page">
      <section className="insights-hero">
        <div className="insights-hero-inner">
          <div>
            <Tag color="blue">{zh ? '资讯' : 'News'}</Tag>
            <Title level={1}>{zh ? 'AI 选型短文' : 'AI buying notes'}</Title>
            <Paragraph>
              {zh
                ? '每篇只写一件事：这款工具干什么、国内能不能直接开、该和谁比。出处链到官网，不写过时的价格。'
                : 'Each note covers one job: what the tool does, how you open it, and what to compare it with. Links go to the vendor site. Prices are omitted because plans change.'}
            </Paragraph>
            {updatedAt ? <Text type="secondary">{zh ? '更新：' : 'Updated: '}{new Date(updatedAt).toLocaleString()}</Text> : null}
          </div>
          <div className="insights-stat-panel">
            <div>
              <strong>{articles.length || '—'}</strong>
              <span>{zh ? '篇' : 'items'}</span>
            </div>
            <NotificationOutlined style={{ fontSize: 28 }} />
          </div>
        </div>
      </section>
      <main className="insights-container">
        <Row gutter={[18, 18]}>
          {articles.map((article) => (
            <Col xs={24} md={12} key={`${article.slug || article.title}-${article.date}`}>
              <article className="insight-card">
                {article.cover ? (
                  <Link to={articlePath(article)}>
                    <img
                      src={article.cover}
                      alt=""
                      style={{ width: '100%', height: 140, objectFit: 'cover', borderRadius: 10, marginBottom: 12 }}
                    />
                  </Link>
                ) : null}
                <div className="insight-card-meta">
                  <Tag>{article.category || 'News'}</Tag>
                  <span>{article.date}</span>
                </div>
                <Title level={4}>
                  <Link to={articlePath(article)}>{article.title}</Link>
                </Title>
                <Paragraph>{article.summary}</Paragraph>
                <Button type="link">
                  <Link to={articlePath(article)}>{zh ? '阅读全文' : 'Read more'}</Link>
                </Button>
              </article>
            </Col>
          ))}
        </Row>
      </main>
    </div>
  );
};

export default NewsPage;
